import { format, parseISO } from "date-fns";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import { Badge } from "../../../../components/ui/badge";
import { Separator } from "../../../../components/ui/separator";
import { ScrollArea } from "../../../../components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../../../../components/ui/sheet";
import { CompanyLogo, getDomainFromCompany } from "../../../../components/ui/CompanyLogo";
import { updateTask } from "../../../../services/tasksEndpoints";
import { useViews } from "../../../../hooks/QueryHooks";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import notify from "../../../../utils/notify";
import type { BoardStatus, TaskRecord } from "../tasksTypes";
import { BOARD_STATUS_LABELS } from "../tasksTypes";

type MemberInfo = { name: string; email: string; initials: string };

type Props = {
  task: TaskRecord;
  open: boolean;
  onClose: () => void;
  members?: any[];
  memberLookup?: Record<string, MemberInfo>;
};

const priorityOptions = ["high", "medium", "low"];
const boardStatusOptions: BoardStatus[] = ["investigating", "needs_first_response", "on_us", "on_them", "on_hold", "closed"];
const statusOptions = ["open", "completed", "closed"];

const priorityBadgeClass: Record<string, string> = {
  high: "border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400",
  medium: "border-yellow-200 bg-yellow-50 text-yellow-700 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-400",
  low: "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-400",
};

const formatRevenue = (value?: number) => {
  if (value == null) return "--";
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
};

const TaskDetailSheet = ({ task, open, onClose, members = [], memberLookup = {} }: Props) => {
  const workspace = useWorkspaceStore((state) => state.workspace);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const params = useParams();
  const { data: viewsData }: any = useViews({ workspaceId: workspace?.id || "" });

  const [editingField, setEditingField] = useState<string | null>(null);

  const defaultAccountViewId = useMemo(() => {
    return viewsData
      ?.filter((v: any) => v?.is_precreated)
      ?.find((v: any) => v?.entity_relation === "accounts")?.id;
  }, [viewsData]);

  const navigateToAccount = () => {
    if (task.account_external_id && defaultAccountViewId && params?.workspaceId) {
      onClose();
      navigate(`/home/${params.workspaceId}/view/${defaultAccountViewId}/account/${task.account_external_id}/overview`);
    }
  };

  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<TaskRecord>) => {
      if (!workspace?.id) throw new Error("No workspace");
      return updateTask({
        workspace_id: workspace.id,
        task_external_id: task.external_id,
        updates,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks-page", workspace?.id] });
      queryClient.invalidateQueries({ queryKey: ["task-board-counts", workspace?.id] });
      notify({ heading: "Task updated", type: "success" });
    },
    onError: () => {
      notify({ heading: "Failed to update task", type: "failure" });
    },
  });

  const handleFieldChange = (field: string, value: string) => {
    setEditingField(null);
    updateMutation.mutate({ [field]: value });
  };

  const companyDomain = task.account_domain || (task.account_name ? getDomainFromCompany(task.account_name) : "");

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="!w-[620px] !max-w-[620px] p-0 flex flex-col">
        <SheetHeader className="px-6 pt-6 pb-4 space-y-3">
          <div className="flex items-start justify-between gap-3 pr-8">
            <div className="min-w-0 flex-1">
              <SheetTitle className="text-base font-semibold text-foreground leading-tight">
                {task.subject || task.native_subject || task.description || "Untitled task"}
              </SheetTitle>
              {(task.native_description || task.evidence_text) && (
                <SheetDescription className="mt-1 text-[13px] line-clamp-3">
                  {task.native_description || task.evidence_text}
                </SheetDescription>
              )}
            </div>
            {task.priority && (
              <Badge
                variant="outline"
                className={`shrink-0 ${priorityBadgeClass[task.priority.toLowerCase()] || ""}`}
              >
                {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
              </Badge>
            )}
          </div>
        </SheetHeader>

        <Separator />

        <ScrollArea className="flex-1">
          <div className="px-6 py-4 space-y-5">
            {/* Fields grid */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              {/* Status */}
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                  Status
                </p>
                <Select
                  value={task.status || "open"}
                  onValueChange={(value) => handleFieldChange("status", value)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[202]">
                    {statusOptions.map((opt) => (
                      <SelectItem key={opt} value={opt}>
                        {opt.charAt(0).toUpperCase() + opt.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Priority */}
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                  Priority
                </p>
                <Select
                  value={task.priority?.toLowerCase() || "medium"}
                  onValueChange={(value) => handleFieldChange("priority", value)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[202]">
                    {priorityOptions.map((opt) => (
                      <SelectItem key={opt} value={opt}>
                        {opt.charAt(0).toUpperCase() + opt.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Board Status */}
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                  Board Status
                </p>
                <Select
                  value={task.board_status || "investigating"}
                  onValueChange={(value) => handleFieldChange("board_status", value)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[202]">
                    {boardStatusOptions.map((opt) => (
                      <SelectItem key={opt} value={opt}>
                        {BOARD_STATUS_LABELS[opt]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Owner Side */}
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                  Owner Side
                </p>
                <div className="text-[13px] text-foreground">
                  {task.owner_side || "--"}
                </div>
              </div>

              {/* Due Date */}
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                  Due Date
                </p>
                <div className="text-[13px] text-foreground">
                  {editingField === "due_date" ? (
                    <input
                      type="date"
                      className="w-full rounded border border-border bg-background px-2 py-1 text-sm"
                      defaultValue={task.due_date ? task.due_date.split("T")[0] : ""}
                      autoFocus
                      onBlur={(e) => {
                        setEditingField(null);
                        if (e.target.value) {
                          handleFieldChange("due_date", e.target.value);
                        }
                      }}
                    />
                  ) : (
                    <button
                      type="button"
                      className="text-left hover:underline"
                      onClick={() => setEditingField("due_date")}
                    >
                      {task.due_date ? format(parseISO(task.due_date), "MMM d, yyyy") : "--"}
                    </button>
                  )}
                </div>
              </div>

              {/* Assigned To */}
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                  Assigned To
                </p>
                <Select
                  value={task.assigned_to || "__unassigned__"}
                  onValueChange={(value) => handleFieldChange("assigned_to", value === "__unassigned__" ? "" : value)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue>
                      {task.assigned_to
                        ? (memberLookup[task.assigned_to]?.name || task.assigned_to)
                        : "Unassigned"}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="z-[202]">
                    <SelectItem value="__unassigned__">Unassigned</SelectItem>
                    {members.map((member: any) => {
                      const memberId = member.member_id || member.user_id;
                      const name = `${member.first_name || ""} ${member.last_name || ""}`.trim() || member.email;
                      return (
                        <SelectItem key={memberId} value={memberId}>
                          {name}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {/* Source Type */}
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                  Source Type
                </p>
                <div className="text-[13px] text-foreground">
                  {task.source_type ? task.source_type.charAt(0).toUpperCase() + task.source_type.slice(1) : "--"}
                </div>
              </div>

              {/* Task Type */}
              {task.task_type && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                    Task Type
                  </p>
                  <div className="text-[13px] text-foreground">
                    {task.task_type.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())}
                  </div>
                </div>
              )}

              {/* Created */}
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                  Created
                </p>
                <div className="text-[13px] text-foreground">
                  {task.created_at ? format(parseISO(task.created_at), "MMM d, yyyy") : "--"}
                </div>
              </div>
            </div>

            {/* Account info section */}
            {task.account_name && (
              <>
                <Separator />
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
                    Account
                  </p>
                  <button
                    type="button"
                    className={`flex items-center gap-3 mb-3 w-full text-left rounded-lg p-2 -m-2 transition-colors ${task.account_external_id && defaultAccountViewId ? "hover:bg-muted cursor-pointer" : ""}`}
                    onClick={navigateToAccount}
                    disabled={!task.account_external_id || !defaultAccountViewId}
                  >
                    <CompanyLogo
                      domain={companyDomain}
                      name={task.account_name}
                      className="h-8 w-8 rounded"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground">{task.account_name}</p>
                      {task.account_domain && (
                        <p className="text-xs text-muted-foreground">{task.account_domain}</p>
                      )}
                    </div>
                    {task.account_external_id && defaultAccountViewId && (
                      <ChevronRight className="h-4 w-4 ml-auto text-muted-foreground flex-shrink-0" />
                    )}
                  </button>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="rounded-lg border border-border bg-muted/50 px-3 py-2">
                      <p className="text-[10px] font-medium text-muted-foreground uppercase">ARR</p>
                      <p className="text-sm font-medium text-foreground">{formatRevenue(task.account_arr)}</p>
                    </div>
                    <div className="rounded-lg border border-border bg-muted/50 px-3 py-2">
                      <p className="text-[10px] font-medium text-muted-foreground uppercase">MRR</p>
                      <p className="text-sm font-medium text-foreground">{formatRevenue(task.account_mrr)}</p>
                    </div>
                    <div className="rounded-lg border border-border bg-muted/50 px-3 py-2">
                      <p className="text-[10px] font-medium text-muted-foreground uppercase">Status</p>
                      <p className="text-sm font-medium text-foreground">
                        {task.account_subscription_status || "--"}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Evidence text section */}
            {task.evidence_text && (
              <>
                <Separator />
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                    Evidence
                  </p>
                  <div className="rounded-lg border border-border bg-muted/30 p-3 text-[13px] text-foreground whitespace-pre-wrap">
                    {task.evidence_text}
                  </div>
                </div>
              </>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

export default TaskDetailSheet;
